import { useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useGetOrganizationByOrgId } from '@/graphql/queries/organizations';
import { useAuthStore } from '@/state/store/auth';
import { OrgShell } from '../components/org-shell';
import { LoadingOverlay } from '@/components/core';
import { Button } from '@/components/ui-kit/button';
import { AlertTriangle, FileQuestion } from 'lucide-react';
import type { OrgRole } from '../org-permissions/types/permissions.types';
import { useMe } from '@/api-client/use-me';
import { useMyOgnizationStore, useUserStore } from '@/store';

const OrgLayoutPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { orgId: routeOrgId } = useParams<{ orgId: string }>();
  const { setOrg, userId, user, orgId } = useAuthStore();
  const iamUser = useUserStore((state) => state.user);
  const currentIamOrg = useMyOgnizationStore((state) => state.currentOrganization);

  const {
    data: udsOrg,
    isLoading: orgLoading,
    isError: orgError,
    isSuccess: orgLoaded,
  } = useGetOrganizationByOrgId(routeOrgId);
  const { data: profile, isLoading: profileLoading } = useMe();
  const sessionUserId = userId ?? iamUser?.itemId ?? profile?.itemId;

  const org = udsOrg ?? (routeOrgId
    ? {
      ItemId: routeOrgId,
      BlocksOrgId: routeOrgId,
      Name: currentIamOrg?.name || 'Organization',
      Slug: routeOrgId,
      MaxUsers: 0,
      MaxTemplates: 0,
    }
    : null);

  const membership = user?.memberships?.find((m) => m.organizationId === org?.BlocksOrgId);
  const iamRole = (iamUser?.roles ?? profile?.roles ?? []).find(
    (role): role is OrgRole =>
      role === 'org_admin' || role === 'org_editor' || role === 'org_member'
  );
  const orgRole = (membership?.roles?.[0] ?? iamRole ?? 'org_member') as OrgRole;
  const canAccessOrg = Boolean(
    org &&
    routeOrgId &&
    (membership ||
      iamUser?.organizationId === org.BlocksOrgId ||
      profile?.organizationId === org.BlocksOrgId ||
      orgId === org.BlocksOrgId)
  );

  useEffect(() => {
    if (orgLoading || !org || !canAccessOrg) return;
    setOrg(org.BlocksOrgId, org.Slug, org.ItemId, org.SigOrgId);
  }, [orgLoading, org, canAccessOrg, setOrg]);

  useEffect(() => {
    if (orgLoading || profileLoading) return;

    if (!sessionUserId) {
      navigate('/login', { replace: true });
      return;
    }

    if (orgLoaded && !routeOrgId) {
      navigate('/org/not-found', { replace: true });
    }
  }, [sessionUserId, orgLoaded, routeOrgId, orgLoading, profileLoading, navigate]);

  if (orgLoading || profileLoading) {
    return <LoadingOverlay />;
  }

  if (orgError && !org) {
    return (
      <div className="flex min-h-[60vh] items-center justify-center">
        <div className="text-center space-y-4">
          <AlertTriangle className="mx-auto h-12 w-12 text-muted-foreground" />
          <h2 className="text-xl font-semibold">
            {t('SOMETHING_WENT_WRONG', { defaultValue: 'Something went wrong' })}
          </h2>
          <p className="text-muted-foreground">
            {t('ORGANIZATION_LOAD_FAILED', {
              defaultValue: "We couldn't load this organization. Please try again.",
            })}
          </p>
          <Button onClick={() => window.location?.reload()}>
            {t('TRY_AGAIN', { defaultValue: 'Try again' })}
          </Button>
        </div>
      </div>
    );
  }

  if (!routeOrgId || !org) {
    return (
      <div className="flex min-h-[60vh] items-center justify-center">
        <div className="text-center space-y-4">
          <FileQuestion className="mx-auto h-12 w-12 text-muted-foreground" />
          <h2 className="text-xl font-semibold">{t('ORGANIZATION_NOT_FOUND')}</h2>
          <p className="text-muted-foreground">
            {t('ORGANIZATION_NOT_FOUND_DESCRIPTION')}
          </p>
          <Button asChild>
            <Link to="/onboarding">{t('GO_HOME')}</Link>
          </Button>
        </div>
      </div>
    );
  }

  if (!canAccessOrg || !orgRole) {
    return <LoadingOverlay />;
  }

  return (
    <OrgShell
      orgName={org.Name}
      orgSlug={org.BlocksOrgId}
      orgId={org.BlocksOrgId}
      orgRole={orgRole}
    />
  );
};

export default OrgLayoutPage;
